import React, { useEffect, useState } from 'react';
import DropdownTreeSelect, { Mode, TreeNode } from 'react-dropdown-tree-select';

import cx from 'classnames';

import { mapBransjeTre } from './utils';

import BransjeApi from 'services/KjederegisteretAdminApi/BransjeApi';
import { BransjeTree } from 'services/KjederegisteretAdminApi/BransjeApi/types';

import useIsMountedRef from 'utils/hooks/useIsMountedRef';

import Style from './index.module.css';
import CommonLoader from 'components/CommonLoader';

export interface PickedBransje {
  id: number;
  name: string;
}

interface Props {
  onSelectionChanged: (bransjer: PickedBransje[]) => void;
  selected?: number[];
  disabled?: boolean;
  isSmall?: boolean;
  mode?: Mode;
  canSelectNedlagt?: boolean;
  /** Når satt vises antall treff (fasett) som suffiks på hver node i treet. */
  facetCounts?: Map<number, number>;
  /** Når satt (sammen med facetCounts) skjules uvalgte noder med 0 treff. */
  hideEmptyFacets?: boolean;
}

const BransjePicker = (props: Props) => {
  const [bransjeTre, setBransjeTre] = useState<BransjeTree[]>([]);
  const [isFetchingBransjer, setIsFetchingBransjer] = useState<boolean>(false);
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());

  const isMountedRef = useIsMountedRef();

  const loadBransjeTre = async () => {
    setIsFetchingBransjer(true);
    const tre = await BransjeApi.getTree();
    setBransjeTre(tre);
    setIsFetchingBransjer(false);
  };

  const handleOnChange = (currentNode: TreeNode, selectedNodes: TreeNode[]) => {
    if (Array.isArray(selectedNodes)) {
      const selectedBransjer = selectedNodes.map((o) => ({ id: o.id, name: o.navn ?? o.label }));
      props.onSelectionChanged(selectedBransjer);
    }
  };

  const handleOnNodeToggle = (currentNode: TreeNode) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (currentNode.expanded) {
        next.add(currentNode.id);
      } else {
        next.delete(currentNode.id);
      }
      return next;
    });
  };

  useEffect(() => {
    if (isMountedRef.current) {
      loadBransjeTre();
    }
  }, []);

  const data = mapBransjeTre(
    bransjeTre,
    props.selected ?? [],
    props.canSelectNedlagt,
    expandedIds,
    props.facetCounts,
    props.hideEmptyFacets,
  );

  return (
    <>
      <DropdownTreeSelect
        className={cx('reactDropdownMultiPicker', Style.multiPicker, {
          [Style.small]: props.isSmall,
        })}
        mode={props.mode || 'multiSelect'}
        texts={{
          placeholder: 'Velg bransje',
          noMatches: 'Ingen treff',
        }}
        data={data || []}
        keepTreeOnSearch
        keepChildrenOnSearch
        showPartiallySelected
        onChange={handleOnChange}
        onNodeToggle={handleOnNodeToggle}
        disabled={props.disabled}
      />
      {isFetchingBransjer && (
        <div>
          <CommonLoader />
          <span> Henter data... </span>
        </div>
      )}
    </>
  );
};

export default React.memo(BransjePicker);
